/**
 * ErrorBoundary global.
 * Captura excepciones en el árbol de React y renderiza ServerError.
 * F8 conecta Sentry (opt-in vía VITE_SENTRY_DSN; ver src/lib/sentry.ts).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ServerError } from '../pages/ServerError';
import { captureException } from '../lib/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Render personalizado (opcional). Recibe el error y un reset. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log local siempre.
    console.error('[ErrorBoundary] caught', error, info.componentStack);
    // Sentry opt-in: si está inicializado (VITE_SENTRY_DSN definido), envía
    // la excepción con el componentStack como contexto. Si no, no-op.
    captureException(error, {
      tags: { source: 'ErrorBoundary' },
      extra: { componentStack: info.componentStack ?? '(unavailable)' },
    });
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return <ServerError error={error} onReset={this.reset} />;
  }
}
